'use client';

import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { CurvedDivider } from './CurvedDivider';
import { HomeDocument, LabelsDocument } from '@/prismicio-types';
import { PrismicRichText } from '@prismicio/react';
import SplashButton from './SplashButton';
import BackgroundBeams from './ui/background-beams';
import { asLink } from '@prismicio/helpers';
import TransitionLink from '@/components/TransitionLink';
import ScrollTracking from './ScrollTracking';
import { usePostHogTracking } from '@/lib/posthog';

function RotatingHeroAwards({ lang }: { lang: string }) {
  const [awardIndex, setAwardIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setAwardIndex((prev) => (prev + 1) % 2);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const awardHref = `/${lang}/blog/one-pacific-wins-coveted-netsuite-erp-expertise-accreditation/`;

  return (
    <div className="flex sm:order-2 sm:col-span-2 sm:justify-center">
      {/* Mobile: stacked side by side */}
      <TransitionLink
        href={awardHref}
        className="mb-8 mt-4 flex items-center justify-center gap-4 sm:hidden"
      >
        <Image
          src="/netsuite-erp-award-laurel-600.png"
          alt="Netsuite ERP Award for OnePacific"
          quality={80}
          width={360}
          height={144}
          className="w-[150px] opacity-80"
        />
        <Image
          src="/award2.png"
          alt="OnePac NetSuite Award"
          quality={80}
          width={600}
          height={211}
          className="w-[150px] opacity-80"
        />
      </TransitionLink>

      {/* Desktop/tablet: cross-fade rotation */}
      <TransitionLink
        href={awardHref}
        className="relative hidden sm:mb-12 sm:block sm:w-[200px] md:w-[280px] xl:w-[320px] 2xl:w-[340px]"
      >
        <Image
          src="/netsuite-erp-award-laurel-600.png"
          alt="Netsuite ERP Award for OnePacific"
          quality={80}
          width={360}
          height={144}
          className={`w-full transition-opacity duration-700 ${
            awardIndex === 0 ? 'opacity-80' : 'opacity-0'
          }`}
        />
        <Image
          src="/award2.png"
          alt="OnePac NetSuite Award"
          quality={80}
          width={600}
          height={211}
          className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-700 ${
            awardIndex === 1 ? 'opacity-80' : 'opacity-0'
          }`}
        />
      </TransitionLink>
    </div>
  );
}

interface HeroSectionProps {
  data: HomeDocument;
  labels: LabelsDocument;
  lang: string;
  // A/B experiment override for the hero H1. When present, renders these segments instead of
  // the Prismic headline; `strong: true` segments get the same `.highlighter strong` effect.
  headlineOverride?: { text: string; strong?: boolean }[];
}

export function HeroSection({ data, labels, lang, headlineOverride }: HeroSectionProps) {
  const { trackCTAClick } = usePostHogTracking();

  // Only disable PostHog for internal traffic
  const isInternalTraffic =
    typeof window !== 'undefined' &&
    document.cookie.split(';').some((cookie) => cookie.trim().startsWith('traffic_type=internal'));
  const shouldUsePostHog = !isInternalTraffic;

  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('HeroSection PostHog check:', {
      isInternalTraffic,
      shouldUsePostHog,
      lang,
      cookies: typeof window !== 'undefined' ? document.cookie : 'server-side',
    });
  }

  const headlineField = data.data.hero_headline;

  const secondaryCtaField = data.data.hero_secondary_cta_button;
  const secondaryCtaUrl = asLink(secondaryCtaField) ?? '';
  const secondaryCtaText = secondaryCtaField?.text ?? '';
  const hasSecondaryCta = Boolean(secondaryCtaUrl && secondaryCtaText);

  const handleHeroClick = (buttonType: 'cta' | 'secondary', buttonName: string) => {
    if (shouldUsePostHog) {
      trackCTAClick('hero_click', 'Home Hero', {
        page: 'home',
        language: lang,
        button_type: buttonType,
        button_name: buttonName,
      });
    }
  };

  return (
    <section className="relative flex min-h-[660px] w-full items-center justify-center overflow-hidden bg-black sm:min-h-[700px] se:h-[100vh]">
      {shouldUsePostHog && (
        <ScrollTracking pageName="home" language={lang} />
      )}
      <BackgroundBeams opacity={0.2} />
      {/* Hero background image: replaces hero video */}
      <Image
        src="/onepac-hongkong-netsuite.jpg"
        alt="Netsuite Hero Background"
        fill
        sizes="100vw"
        quality={60}
        className="absolute inset-0 z-0 h-full w-full object-cover opacity-40 sm:opacity-50"
        priority
        draggable={false}
      />
      <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-r from-black" />
      <div className="absolute inset-0 bg-black bg-opacity-5" />
      <div className="relative flex h-full w-full items-center">
        <div className="container pt-8 2xl:pl-[8rem]">
          <div className="mx-auto grid max-w-6xl grid-cols-1 items-center sm:grid-cols-5">
            <RotatingHeroAwards lang={lang} />
            <div className="w-full max-w-[320px] sm:max-w-[380px] text-white sm:order-1 sm:col-span-3 lg:max-w-full">
              <h1 className="highlighter highlighter-lighter text-[1.9rem] font-semibold leading-[1.25] sm:max-w-full sm:text-4xl sm:font-normal sm:leading-[1.2] lg:text-5xl">
                {headlineOverride ? (
                  headlineOverride.map((seg, i) =>
                    seg.strong ? (
                      <strong key={i}>{seg.text}</strong>
                    ) : (
                      <React.Fragment key={i}>{seg.text}</React.Fragment>
                    ),
                  )
                ) : (
                  <PrismicRichText
                    field={headlineField}
                    components={{
                      paragraph: ({ children }) => <>{children}</>,
                    }}
                  />
                )}
              </h1>
              <div className="hero-description mt-4 text-base tracking-wide text-[#ccc] lg:w-[90%] leading-[1.75]">
                <PrismicRichText field={data.data.hero_rich_description} />
                <div className="items-left flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-6">
                  <SplashButton
                    url={`/${lang}/contact`}
                    color="red"
                    data-location="Home Hero"
                    data-event="hero_click"
                    data-ph-button-type="cta"
                    data-ph-button-name="Contact CTA"
                    data-ph-location="Home Hero"
                    size="md"
                    text={data.data.hero_cta_button || ''}
                    className="ga-contact ph-convert mt-10 min-w-[300px] sm:mt-14"
                    onClick={() => handleHeroClick('cta', 'Contact CTA')}
                  />
                  {hasSecondaryCta && (
                    <SplashButton
                      url={secondaryCtaUrl}
                      color="transparent"
                      data-location="Home Hero"
                      data-event="hero_click"
                      data-ph-button-type="secondary"
                      data-ph-button-name={secondaryCtaText}
                      data-ph-location="Home Hero"
                      size="md"
                      text={secondaryCtaText}
                      className="hero-invisible ph-convert mt-4 min-w-[260px] ring-white/10 sm:mt-14 hidden sm:flex"
                      onClick={() => handleHeroClick('secondary', secondaryCtaText)}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="hero-hero absolute bottom-10 right-0 hidden text-right text-xs text-gray-400">
        <p>
          <b>Jim Tornqvist</b>
        </p>
        <p>3Doodler - B2B Director</p>
        <p>OnePac since 2017</p>
      </div>

      <CurvedDivider color="#F3F4F6" />
    </section>
  );
}
